import { ReferenceField, FunctionField, useRecordContext } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { EventType, Event } from '../../types';
import { MaterialChangedTagDisplay } from './MaterialChangedTagDisplay';
import { SensorStatusDisplay } from './SensorStatusDisplay';

const PelletDispenseInfoContent = ({
  eventType,
  record,
}: {
  record?: Event;
  eventType: EventType;
}) => {
  if (!record?.rawContent) return null;
  if (eventType.name !== 'pellet-dispense') return null;

  try {
    const rawData = JSON.parse(record.rawContent);
    const fields = rawData?.fields;
    const tagNumber = fields?.tag;
    const tagInfo = fields?.tagInfo;
    let dispenserNumber: number | null = fields?.dispenser;
    const position = fields?.klipperConStatus?.cncState?.pos;
    const sensorStatus = fields?.sensorStatus;
    const disableScratchDetector = fields?.disableScratchDetector;

    // Extract dispenser number from device (RFID-READER-<numero>)
    if (fields.device && typeof fields.device === 'string') {
      const match = fields.device.match(/RFID-READER-(\d+)/);
      if (match) {
        dispenserNumber = parseInt(match[1], 10) + 1;
      }
    }

    if (
      !tagNumber &&
      !tagInfo &&
      !dispenserNumber &&
      !position &&
      !sensorStatus &&
      disableScratchDetector === undefined
    )
      return null;

    return (
      <Box sx={{ p: 2, display: 'inline-flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="subtitle2">Pellet Dispense</Typography>
          {dispenserNumber !== null && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              • Dispenser {dispenserNumber}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          {position && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Position
              </Typography>
              <Box sx={{ display: 'inline-flex', gap: 3 }}>
                {position.X !== undefined && (
                  <Typography variant="body2">
                    <strong>X:</strong> {parseFloat(position.X).toFixed(2)} mm
                  </Typography>
                )}
                {position.Y !== undefined && (
                  <Typography variant="body2">
                    <strong>Y:</strong> {parseFloat(position.Y).toFixed(2)} mm
                  </Typography>
                )}
                {position.Z !== undefined && (
                  <Typography variant="body2">
                    <strong>Z:</strong> {parseFloat(position.Z).toFixed(2)} mm
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          <SensorStatusDisplay
            funnel={sensorStatus?.funnel}
            pellets={sensorStatus?.pellets}
            up={sensorStatus?.up}
          />
          {disableScratchDetector !== undefined && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}
              >
                Scratch Detector
              </Typography>
              <Typography variant="body2">
                <Box
                  component="span"
                  sx={{
                    color: disableScratchDetector === 'true' ? 'error.main' : 'success.main',
                    fontWeight: 600,
                  }}
                >
                  {disableScratchDetector === 'true' ? 'Disabled' : 'Enabled'}
                </Box>
              </Typography>
            </Box>
          )}
          {tagInfo && (
            <Box
              sx={{
                p: 2,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <MaterialChangedTagDisplay
                tag={{ number: tagNumber, info: { ...tagInfo } }}
                label="Tag Information"
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  } catch {
    // Error parsing rawContent, silently ignore
    return null;
  }
};

export const PelletDispenseInfo = () => {
  const record = useRecordContext<Event>();
  return (
    <ReferenceField source="eventTypeId" reference="event-types" link={false}>
      <FunctionField
        render={(eventType: EventType) => (
          <PelletDispenseInfoContent record={record} eventType={eventType} />
        )}
      />
    </ReferenceField>
  );
};
